import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router'
import {
  isUndefined, isObject, isEmpty, isNil, startCase, toPairs, join, map, includes, chunk
} from 'lodash'
import { DetailList, FormattedDate, Tooltip, TooltipTrigger, TooltipContent } from '@ideon-api/ideon-ui'

// PD-30615 MR3: metadata carries no grouping (unlike Benefits), so per the
// 2026-09-18 layout decision this renders two ungrouped columns rather than
// an unvalidated taxonomy. Split down the middle, first half then second --
// reading column 1 top-to-bottom then column 2 preserves the original order.
const COLUMN_GRID_CLASS = 'grid grid-cols-1 md:grid-cols-2 gap-x-6'

export class PlanInformationComponent extends Component {
  static propTypes = {
    plan: PropTypes.object.isRequired,
    fieldGroups: PropTypes.object.isRequired
  }

  get metadataFields() {
    const fieldNames = {}

    toPairs(this.props.fieldGroups['Plan Info'].metadatum).forEach((field) => {
      if (!field[1].display_on_cvt) return

      fieldNames[field[1].vericred_name || field[0]] = {
        label: field[1].cvt_label || field[1].label,
        type: field[1].type
      }
    })

    const orderedFieldNames = Object.assign(this.metadataFieldsOrder, fieldNames)

    return Object.assign(orderedFieldNames, { plan_documents: { label: 'Plan Documents' } })
  }

  get metadataFieldsOrder() {
    if (this.props.plan.product_line === 'life') return this.lifeMetadataOrder

    return {}
  }

  get lifeMetadataOrder() {
    return {
      name: null,
      voluntary: null,
      accidental_death_dismemberment_included: null,
      insured_benefits: null
    }
  }

  stringifyObject = (value) => {
    const pairs = []
    Object.keys(value).forEach((key) => { pairs.push(`${startCase(key)}: ${value[key]}`) })

    return join(pairs, ' / ')
  }

  sanitizeValue = (val) => {
    if (isObject(val)) return this.stringifyObject(val)

    const value = isUndefined(val) ? '' : val.toString()

    // PD-30709: a URL is one unbreakable word, so it needs a scroll container plus a
    // tooltip carrying the full value -- a plain string or a FormattedDate does not.
    if (value.match(/^https?:/)) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            {/* Scrolls rather than truncates, so the URL can still be selected and
              * copied. The scroll sits on the link, not the cell: axe's
              * scrollable-region-focusable needs a scroll container to be
              * keyboard-reachable, and a bare cell is not.
              * rel is explicit, not implied: these URLs come from carrier-supplied
              * plan metadata, so the opener must be isolated in older browsers too. */}
            <Link
              to={value}
              target="_blank"
              rel="noopener noreferrer"
              className="block overflow-x-auto whitespace-nowrap"
            >
              {value}
            </Link>
          </TooltipTrigger>
          {/* TooltipContent is w-fit max-w-xs with no break rule, so a long URL
            * would overflow the bubble. break-words, not break-all: it takes the
            * slash and hyphen breakpoints a URL offers before splitting mid-token. */}
          <TooltipContent className="break-words">{value}</TooltipContent>
        </Tooltip>
      )
    }
    if (value.match(/^\d/)) return value
    if (value === 'true') return 'Yes'
    if (value === 'false') return 'No'

    return startCase(value).replace('Both Markets', 'Both')
  }

  sanitizeBenefit = (val) => {
    const defaultOrder = { maximum: null, minimum: null, increment: null }

    return this.stringifyObject(Object.assign(defaultOrder, val))
  }

  addBenefits = (name, benefits) => {
    const defaultOrder = { employee: null, spouse: null, child: null }

    return map(Object.assign(defaultOrder, benefits), (value, key) => ({
      testId: `plan-info-insured-benefit-${key}`,
      label: startCase(`${key} ${name}`),
      // isNil as well as '': a null or undefined benefit would otherwise reach
      // sanitizeBenefit and be interpolated into the string as text.
      value: isNil(value) || value === '' ? '–' : this.sanitizeBenefit(value)
    }))
  }

  addPlanDocuments = () => {
    const planDocuments = this.props.plan.metadata.plan_documents

    if (isEmpty(planDocuments)) { return [] }

    return map(planDocuments, (value, key) => ({
      testId: `plan-info-document-${key}`,
      label: startCase(value.type),
      value: this.sanitizeValue(value.url)
    }))
  }

  // One {testId, label, value} row per metadata field -- or several, for the
  // two fields (Plan Documents, an insured-benefits group) that expand into
  // more than one row. Flattened here so the 2-column split downstream just
  // divides a plain array, with no special-casing at render time.
  rows = () => {
    const { plan } = this.props
    const metadata = plan.metadata
    const rows = []

    toPairs(this.metadataFields).forEach(([fieldKey, fieldMeta]) => {
      const label = fieldMeta ? fieldMeta.label : null

      if (label === 'Plan Documents') {
        rows.push(...this.addPlanDocuments())
        return
      }

      if (includes(label, 'Benefits')) {
        rows.push(...this.addBenefits(label, plan.benefits))
        return
      }

      const rawValue = metadata[fieldKey]
      // Name keeps its raw value rather than going through startCase, but it
      // still needs the missing-value mapping the other fields get.
      const value = label === 'Name'
        ? this.rawOrPlaceholder(rawValue)
        : this.valueForField(fieldMeta, rawValue)

      rows.push({ testId: `plan-info-${plan.id}-${fieldKey}`, label, value })
    })

    return rows
  }

  rawOrPlaceholder = (rawValue) => (isNil(rawValue) || rawValue === '' ? '–' : rawValue)

  valueForField = (fieldMeta, rawValue) => {
    if (isNil(rawValue) || rawValue === '') return '–'

    // FormattedDate handles the em-dash/UTC-formatting itself; only reached
    // once a real value is confirmed present.
    if (fieldMeta && fieldMeta.type === 'date') return <FormattedDate value={rawValue} />

    return this.sanitizeValue(rawValue)
  }

  renderColumn = (rows, columnIndex) => (
    <DetailList.Root key={`plan-info-column-${columnIndex}`}>
      {rows.map((row) => (
        <DetailList.Item key={row.testId} label={row.label} value={row.value} data-testid={row.testId} />
      ))}
    </DetailList.Root>
  )

  render() {
    const { plan } = this.props
    const rows = this.rows()
    const half = Math.ceil(rows.length / 2)
    const columns = chunk(rows, half)

    return (
      <div id={`${plan.id}-info`}>
        <div className={COLUMN_GRID_CLASS}>
          {columns.map((columnRows, index) => this.renderColumn(columnRows, index))}
        </div>
      </div>
    )
  }
}

export default PlanInformationComponent
